import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable per-service notification email editor.
 * service: "events" | "jobs" | "newsletter"
 */
export default function NotifyEmailField({ service, label }) {
  const [email, setEmail] = useState("");
  const [original, setOriginal] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/notify-emails`, { withCredentials: true });
      const v = data[service] || "";
      setEmail(v);
      setOriginal(v);
    } catch (err) {
      console.error("notify-email load error:", err);
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setError(""); setSaving(true); setSaved(false);
    try {
      await axios.put(`${API}/settings/notify-emails/${service}`, { email }, { withCredentials: true });
      setOriginal(email);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const dirty = email.trim() !== original.trim();

  return (
    <div
      data-testid={`notify-email-${service}`}
      className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4 sm:p-5 mb-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2 flex-shrink-0">
          <Mail className="w-4 h-4 text-[#C5993A]" />
          <label className="text-sm text-white font-medium">{label || "Empfänger-Mailadresse"}</label>
        </div>
        <Input
          data-testid={`notify-email-input-${service}`}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={loading ? "Lädt..." : "name@example.com"}
          disabled={loading || saving}
          className="bg-[#0C1620] border-[#3A4F60]/50 text-white focus:border-[#C5993A] flex-1 h-9"
        />
        <Button
          data-testid={`notify-email-save-${service}`}
          onClick={save}
          disabled={!dirty || saving || loading}
          size="sm"
          className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 h-9 px-4 flex-shrink-0"
        >
          {saved ? <Check className="w-4 h-4" /> : saving ? "Speichere..." : "Speichern"}
        </Button>
      </div>
      <p className="text-xs text-[#3A4F60] mt-2">
        Automatische Benachrichtigungen dieses Bereichs werden an diese Adresse gesendet.
      </p>
      {error && <p data-testid={`notify-email-error-${service}`} className="text-red-400 text-xs mt-2">{error}</p>}
    </div>
  );
}
